import React, { useState, useEffect } from 'react';
import collegeConfig from '../../config/collegeConfig';
import { FaToggleOn, FaToggleOff, FaSpinner, FaCopy, FaCheck, FaSync, FaInfoCircle } from 'react-icons/fa';

const API = collegeConfig.api.baseURL;

const AttendanceSettings = () => {
  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [toast, setToast] = useState('');
  const [copied, setCopied] = useState('');

  const token = localStorage.getItem('token');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  useEffect(() => {
    fetch(`${API}/system-settings/attendance-key`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setEnabled(d.attendanceIntegrationEnabled);
          setApiKey(d.attendanceApiKey);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const toggle = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/system-settings/attendance-integration`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: !enabled })
      });
      const d = await r.json();
      if (d.success) {
        setEnabled(d.attendanceIntegrationEnabled);
        setApiKey(d.attendanceApiKey);
        showToast(`Attendance integration ${d.attendanceIntegrationEnabled ? 'enabled' : 'disabled'}`);
      } else {
        showToast(d.message || 'Failed to update');
      }
    } catch {
      showToast('Failed to update');
    }
    setSaving(false);
  };

  const regenerateKey = async () => {
    if (!window.confirm('Regenerate the API key? Attendance Provider will need to be given the new key.')) return;
    setRegenerating(true);
    try {
      const r = await fetch(`${API}/system-settings/attendance-key/regenerate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success) {
        setApiKey(d.attendanceApiKey);
        showToast('API key regenerated — share the new key with Attendance Provider');
      } else {
        showToast(d.message || 'Failed to regenerate');
      }
    } catch {
      showToast('Failed to regenerate key');
    }
    setRegenerating(false);
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(field);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const endpointUrl = `${API}/attendance/inbound`;

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
      <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: 28, color: '#b11217' }} />
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={s.container}>
      {toast && <div style={s.toast}>{toast}</div>}
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

      <div style={s.pageHeader}>
        <div>
          <h1 style={s.title}>Attendance Integration</h1>
          <p style={s.subtitle}>Allow Attendance Provider ERP to push daily attendance data into the E-Logbook</p>
        </div>
      </div>

      {/* Toggle */}
      <div style={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e', marginBottom: 4 }}>
              Attendance Integration
            </div>
            <div style={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>
              When enabled, Attendance Provider can POST daily attendance data to your API endpoint.
              Students will see a "Fill Logbook based on Attendance" option on their dashboard.
            </div>
          </div>
          <button
            onClick={toggle}
            disabled={saving}
            style={{ background: 'none', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', padding: 0, flexShrink: 0, marginLeft: 24 }}
          >
            {saving
              ? <FaSpinner style={{ fontSize: 36, color: '#b11217', animation: 'spin 1s linear infinite' }} />
              : enabled
                ? <FaToggleOn style={{ fontSize: 42, color: '#059669' }} />
                : <FaToggleOff style={{ fontSize: 42, color: '#ccc' }} />
            }
          </button>
        </div>

        <div style={{
          marginTop: 14,
          padding: '8px 14px',
          borderRadius: 8,
          background: enabled ? '#d1fae5' : '#f3f4f6',
          color: enabled ? '#065f46' : '#6b7280',
          fontSize: 13,
          fontWeight: 600,
          display: 'inline-block'
        }}>
          {enabled ? 'Currently Enabled' : 'Currently Disabled'}
        </div>
      </div>

      {/* API Details — only show when enabled */}
      {enabled && apiKey && (
        <>
          <div style={{ ...s.card, borderLeft: '4px solid #6C3483' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 16 }}>
              <FaInfoCircle style={{ color: '#6C3483', marginTop: 2, flexShrink: 0 }} />
              <div style={{ fontSize: 13, color: '#444', lineHeight: 1.6 }}>
                Share the <strong>Endpoint URL</strong> and <strong>API Key</strong> below with Attendance Provider.
                They must include the key as an <code style={s.code}>X-Attendance-API-Key</code> header when calling the endpoint.
                Data should be sent daily at midnight using HTTP <strong>POST</strong>.
              </div>
            </div>

            <div style={s.fieldRow}>
              <div style={s.fieldLabel}>POST Endpoint URL</div>
              <div style={s.fieldBox}>
                <span style={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>{endpointUrl}</span>
                <button style={s.copyBtn} onClick={() => copyToClipboard(endpointUrl, 'url')}>
                  {copied === 'url' ? <FaCheck style={{ color: '#059669' }} /> : <FaCopy />}
                </button>
              </div>
            </div>

            <div style={s.fieldRow}>
              <div style={s.fieldLabel}>API Key (X-Attendance-API-Key)</div>
              <div style={s.fieldBox}>
                <span style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', letterSpacing: 1 }}>{apiKey}</span>
                <button style={s.copyBtn} onClick={() => copyToClipboard(apiKey, 'key')}>
                  {copied === 'key' ? <FaCheck style={{ color: '#059669' }} /> : <FaCopy />}
                </button>
              </div>
            </div>

            <div style={s.fieldRow}>
              <div style={s.fieldLabel}>Required Header</div>
              <div style={s.fieldBox}>
                <span style={{ fontFamily: 'monospace', fontSize: 13 }}>X-Attendance-API-Key: {'<key above>'}</span>
              </div>
            </div>

            <button onClick={regenerateKey} disabled={regenerating} style={s.regenBtn}>
              {regenerating
                ? <FaSpinner style={{ marginRight: 8, animation: 'spin 1s linear infinite' }} />
                : <FaSync style={{ marginRight: 8 }} />
              }
              Regenerate Key
            </button>
          </div>

          <div style={{ ...s.card, background: '#fffbeb', borderLeft: '4px solid #f59e0b' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#92400e', marginBottom: 8 }}>Expected Data Format</div>
            <div style={{ fontSize: 12, color: '#78350f', lineHeight: 1.6 }}>
              Attendance Provider should send a JSON body with a <code style={s.code}>records</code> array.
              Each record must include: <code style={s.code}>registration_number</code>, <code style={s.code}>student_name</code>,{' '}
              <code style={s.code}>lecture_name</code>, <code style={s.code}>attendance_status</code> (Present / Absent / On Duty),{' '}
              <code style={s.code}>faculty_name</code>, <code style={s.code}>date_of_attendance</code> (YYYY-MM-DD).
              They should send <strong>today's data + data from 2 days ago</strong> (48-hour modification window).
              Our system will upsert records — if attendance was modified, it updates the existing record.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const s = {
  container: { maxWidth: 760, margin: '0 auto', padding: '24px 16px' },
  pageHeader: { marginBottom: 24 },
  title: { margin: 0, fontSize: 22, fontWeight: 800, color: '#1a1a2e' },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: '#666' },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  fieldRow: { marginBottom: 14 },
  fieldLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: '#6b7280', marginBottom: 6 },
  fieldBox: { display: 'flex', alignItems: 'center', gap: 10, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px' },
  copyBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6C3483', flexShrink: 0, fontSize: 15, padding: 4 },
  regenBtn: { display: 'inline-flex', alignItems: 'center', marginTop: 8, padding: '8px 18px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', color: '#374151' },
  code: { background: '#f3f4f6', padding: '1px 5px', borderRadius: 4, fontFamily: 'monospace', fontSize: 12 },
  toast: { position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#1a1a2e', color: '#fff', padding: '10px 20px', borderRadius: 20, fontSize: 14, zIndex: 2000, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }
};

export default AttendanceSettings;
